import { ORIENTATION, Point, Polygon, Segment, Vector } from '@flatten-js/core';
import { EditState } from './edit-state/edit-state';
import {
  constrainSegmentToBorders,
  checkIfContains,
  isInnerBorder,
  isOuterBorder,
  rotationMatrix,
  smallestAngleBetween,
} from './geometrical/calc-auto-breakerboard';
import { Requester } from './requester';

const RAD90 = Math.PI / 2;

/**
 * Creates flatten-js polygons out of the 'Grundriss' (ground plan) data.
 * @param {Object[]} groundPlan - Output of 'Grundriss' step. Each object of the array must have
 * a {{x,y}[]} points element and a {string} cutout element.
 * @returns {Flatten.Polygon[]} - Array of polygons, with borders in CW direction and holes in CCW.
 */
function createPolygons(groundPlan) {
  const polygons = [];
  const cutOutPolygons = [];
  for (const area of groundPlan) {
    const polygon = new Polygon();
    polygon.addFace(area.points.map(p => new Point(p.x, p.y)));
    if (area.cutout === 'YES') {
      if ([...polygon.faces][0].orientation() !== ORIENTATION.CCW) {
        [...polygon.faces][0].reverse();
      }
      cutOutPolygons.push(polygon);
    } else {
      if ([...polygon.faces][0].orientation() !== ORIENTATION.CW) {
        [...polygon.faces][0].reverse();
      }
      polygons.push(polygon);
    }
  }

  for (const cutOut of cutOutPolygons) {
    const cutOutVertex = cutOut.vertices[0];
    for (const poly of polygons) {
      if (cutOutVertex.on(poly)) {
        poly.addFace([...cutOut.faces][0]);
        break;
      }
    }
  }

  return polygons;
}

export function createMinimalData(groundPlan) {
  const rawAreas = [];
  const polygons = {};
  const cutOutPolygons = [];
  for (const area of groundPlan) {
    const polygon = new Polygon();
    polygon.addFace(area.points.map(p => new Point(p.x, p.y)));
    if (area.cutout === 'YES') {
      cutOutPolygons.push(polygon);
    } else {
      const id = area.descriptor.id;
      polygons[id] = polygon;
      area.active_edge = 0;
      rawAreas.push({ data: area, tessellation: { cutoutPolygons: [] } });
    }
  }
  for (const cutOut of cutOutPolygons) {
    const cutOutVertex = cutOut.vertices[0];
    for (const [id, poly] of Object.entries(polygons)) {
      if (cutOutVertex.on(poly)) {
        if ([...poly.faces][0].orientation() === [...cutOut.faces][0].orientation()) {
          [...cutOut.faces][0].reverse();
        }
        const parent = rawAreas.find(area => area.data.descriptor.id === id);
        parent.tessellation.cutoutPolygons.push({
          points: cutOut.vertices.map(vertex => ({ x: vertex.x, y: vertex.y })),
        });
        break;
      }
    }
  }
  return rawAreas;
}

function getSecondaryBreakerBoardEdge(segment, polygonOrientation, breakerboardWidth) {
  const edgeVector = new Vector(segment.ps, segment.pe);
  const startVector = new Vector(new Point(), segment.ps);
  const endVector = new Vector(new Point(), segment.pe);
  let displacement;
  if (polygonOrientation === ORIENTATION.CW) {
    displacement = edgeVector.rotate90CW();
  } else {
    displacement = edgeVector.rotate90CCW();
  }
  displacement = displacement.normalize().multiply(breakerboardWidth);
  const startDisplacedVector = startVector.add(displacement);
  const endDisplacedVector = endVector.add(displacement);
  return new Segment(
    new Point(startDisplacedVector.x, startDisplacedVector.y),
    new Point(endDisplacedVector.x, endDisplacedVector.y)
  );
}

/**
 *
 *
 * @param {Flatten.Polygon} polygon
 * @param {number} planksAngle - In rad
 * @param {number} [minInnerLineLength=20] - Sides shorter than this value will be ignored
 * @param {number} [angleTolerance=0.0001] - Lines are considered parallel if the angle between them is smaller than this value
 * @returns {Flatten.Segment[]} An array of segments considered inner lines (Breakerboard placement candidates)
 */
function getPerpendicularEdges(
  polygon,
  planksAngle,
  polygonOrientation,
  minInnerLineLength = 20,
  angleTolerance = 0.0001
) {
  // Recommended Inner lines (aka 'Breakerboard') are always perpendicular to Planks
  // the polygon perimeter direction CW or CCW is important to identify dents
  const isPolygonCW = polygonOrientation === ORIENTATION.CW;
  const breakerboardVector = new Vector(Math.cos(planksAngle + RAD90), Math.sin(planksAngle + RAD90));
  let edgeVectors = [];
  for (const edge of [...polygon.faces][0]) {
    edgeVectors.push(new Vector(edge.shape.ps, edge.shape.pe));
  }
  edgeVectors = [edgeVectors.slice(-1)[0], ...edgeVectors, edgeVectors[0]];
  const polyVertices = [...polygon.vertices, polygon.vertices[0]];
  const innerEdges = [];
  const outerEdges = [];

  for (let i = 1; i < edgeVectors.length - 1; i++) {
    if (smallestAngleBetween(edgeVectors[i], breakerboardVector) <= angleTolerance) {
      // only dents produce valid inner breakerboard
      if (
        edgeVectors[i].length >= minInnerLineLength &&
        isInnerBorder(edgeVectors[i], edgeVectors[i - 1], edgeVectors[i + 1], isPolygonCW)
      ) {
        innerEdges.push(new Segment(polyVertices[i - 1], polyVertices[i]));
      } else if (isOuterBorder(edgeVectors[i], edgeVectors[i - 1], edgeVectors[i + 1], isPolygonCW)) {
        outerEdges.push(new Segment(polyVertices[i - 1], polyVertices[i]));
      }
    }
  }
  return [innerEdges, outerEdges];
}

function areaParamsFromSplit(originalAreaParam, newAreaStates) {
  return newAreaStates.map(areState => {
    const polygon = areState.getFlattenPolygonOfOriginal(true);
    const breakerBoardsEdges = originalAreaParam.breakerBoardsEdges.filter(prevAreaBB => {
      for (const edge of [...polygon.faces][0]) {
        if (edge.shape.equalTo(prevAreaBB.edge) || edge.shape.equalTo(prevAreaBB.edge.reverse())) {
          return true;
        }
      }
      return false;
    });
    return {
      polygon,
      polyOrientation: [...polygon.faces][0].orientation(),
      id: areState.id,
      breakerBoardsEdges,
    };
  });
}

function getLongestRawAreaEdgeIndex(rawArea) {
  const vertices = rawArea.data.points.map(p => new Point(p.x, p.y));
  vertices.push(vertices[0]);
  let maxIndex;
  let maxLength = 0;
  for (let i = 0; i < rawArea.data.points.length; i++) {
    const edgeLength = vertices[i].distanceTo(vertices[i + 1])[0];
    if (edgeLength > maxLength) {
      maxIndex = i;
      maxLength = edgeLength;
    }
  }
  return maxIndex;
}

function getRotatedBoundingBox(polygon, angle, breakerBoardsEdges = []) {
  const rotatedPolygon = polygon.transform(rotationMatrix(-angle));
  const rotatedBox = rotatedPolygon.box;
  const linesX = breakerBoardsEdges.map(edge => {
    const rotatedEdge = edge.futureCut.transform(rotationMatrix(-angle));
    return rotatedEdge.ps.x;
  });
  let mostLeft;
  let mostRight;
  if (linesX.length === 0) {
    mostLeft = rotatedBox.xmin;
    mostRight = rotatedBox.xmax;
  } else if (linesX.length === 1) {
    if (linesX[0] - rotatedBox.xmin > rotatedBox.xmax - linesX[0]) {
      mostLeft = rotatedBox.xmin;
      mostRight = linesX[0];
    } else {
      mostLeft = linesX[0];
      mostRight = rotatedBox.xmax;
    }
  } else {
    mostLeft = Math.min(...linesX);
    mostRight = Math.max(...linesX);
  }

  const rotatedWidth = new Segment(new Point(mostLeft, rotatedBox.ymin), new Point(mostRight, rotatedBox.ymin));
  const rotatedHeight = new Segment(new Point(mostLeft, rotatedBox.ymin), new Point(mostLeft, rotatedBox.ymax));

  const width = rotatedWidth.transform(rotationMatrix(angle));
  const widthVector = new Vector(width.ps, width.pe);
  const height = rotatedHeight.transform(rotationMatrix(angle));
  const heightVector = new Vector(height.ps, height.pe);
  const xminyminVector = new Vector(new Point(0, 0), width.ps);
  const xminymin = width.ps;
  const xmaxymin = width.pe;
  const xminymax = height.pe;
  const xmaxymaxVector = xminyminVector.add(widthVector).add(heightVector);
  const xmaxymax = new Point(xmaxymaxVector.x, xmaxymaxVector.y);
  return { xminymin, xmaxymin, xminymax, xmaxymax };
}

function getAreaParamCloserToBorder(resultAreaParams, border) {
  const distances = resultAreaParams.map(resultAreaParam => border.distanceTo(resultAreaParam.polygon)[0]);
  const minDistance = Math.min(...distances);
  const indexOfMin = distances.findIndex(distance => distance === minDistance);
  return resultAreaParams[indexOfMin];
}

function createSplitsForTooWideAreas(
  islandAreasParam,
  editState,
  maxPlankLength,
  breakerboardWidth,
  islandPlanksAngle
) {
  const areasToCheck = [...islandAreasParam];
  const areasIdToRemove = [];
  for (const areaParam of areasToCheck) {
    const box = getRotatedBoundingBox(areaParam.polygon, islandPlanksAngle, areaParam.breakerBoardsEdges);
    const maxDistance = Math.abs(box.xminymin.distanceTo(box.xmaxymin)[0]);
    if (maxDistance > maxPlankLength + breakerboardWidth) {
      let numDivisions = Math.ceil(maxDistance / maxPlankLength);
      if (
        numDivisions > 2 &&
        (numDivisions - 1) * maxPlankLength + (numDivisions - 2) * breakerboardWidth >= maxDistance
      ) {
        numDivisions = numDivisions - 1;
      }
      const xminSide = new Segment(box.xminymin, box.xminymax);
      const xminyminVector = new Vector(new Point(0, 0), box.xminymin);
      const xminymaxVector = new Vector(new Point(0, 0), box.xminymax);
      const displacement = new Vector(box.xminymin, box.xmaxymin)
        .normalize()
        .multiply((maxDistance - (numDivisions - 1) * breakerboardWidth) / numDivisions + breakerboardWidth);
      const widthCutStartVector = xminyminVector.add(displacement);
      const widthCutEndVector = xminymaxVector.add(displacement);
      const widthCut = new Segment(
        new Point(widthCutStartVector.x, widthCutStartVector.y),
        new Point(widthCutEndVector.x, widthCutEndVector.y)
      );
      const cuts = constrainSegmentToBorders(widthCut, areaParam.polygon);
      const allResultAreaParams = [];
      let nextAreaParamToCut = areaParam;
      // recursively cut areas
      for (const [cutIndex, cut] of cuts.entries()) {
        const resultAreasState = editState.split(nextAreaParamToCut.id, cut);
        const resultAreaParams = areaParamsFromSplit(nextAreaParamToCut, resultAreasState);
        getAreaParamCloserToBorder(resultAreaParams, xminSide).breakerBoardsEdges.push({
          edge: cut,
          futureCut: cut,
        });
        if (cutIndex !== cuts.length - 1) {
          for (const resultAreaParam of resultAreaParams) {
            if (checkIfContains(resultAreaParam.polygon, cuts[cutIndex + 1])) {
              nextAreaParamToCut = resultAreaParam;
              break;
            }
          }
          allResultAreaParams.push(...resultAreaParams.filter(areaParam => areaParam !== nextAreaParamToCut));
        } else {
          allResultAreaParams.push(...resultAreaParams);
        }
      }
      areasIdToRemove.push(areaParam.id);
      areasToCheck.push(...allResultAreaParams);
    }
  }
  areasIdToRemove.forEach(id => areasToCheck.splice(areasToCheck.findIndex(param => param.id === id), 1));
  return areasToCheck;
}

export function createBreakerboardsInstallation(
  rawAreas,
  onSuccess,
  onError,
  priorityShortestBreakerboard = true,
  addClosingBreakerboards = true
) {
  const breakerboardWidth = rawAreas[0].data.boardwidth;
  const maxPlankLength = Math.max(...rawAreas[0].data.boards.map(boards => boards.length));

  for (const island of rawAreas) {
    island.data.active_edge = getLongestRawAreaEdgeIndex(island);
  }
  const requester = new Requester();
  requester.openLoader();
  const editState = new EditState({ rawAreas });

  const startAreasIds = editState.areas.map(areaState => areaState.id);

  // will store areas for each island
  const islands = [];
  for (const id of startAreasIds) {
    const areaState = editState.findAreaById(id);
    const initialPolygon = areaState.getFlattenPolygonOfOriginal(true);
    const polyOrientation = [...initialPolygon.faces][0].orientation();
    const islandPlanksAngle = [...[...initialPolygon.faces][0]][areaState.rawArea.data.active_edge].shape.slope;
    const initialAreaParam = {
      polygon: initialPolygon,
      polyOrientation,
      id,
      breakerBoardsEdges: [],
    };
    let islandAreasParam = [];

    // are breaker boards even needed?
    const box = getRotatedBoundingBox(initialPolygon, islandPlanksAngle);
    if (Math.abs(box.xmaxymin.distanceTo(box.xminymin)[0]) > maxPlankLength) {
      // find all edges perpendicular to planks
      const [innerEdges, outerEdges] = getPerpendicularEdges(initialPolygon, islandPlanksAngle, polyOrientation);
      if (addClosingBreakerboards) {
        initialAreaParam.breakerBoardsEdges.push(
          ...outerEdges.map(edge => {
            return {
              edge,
              futureCut: getSecondaryBreakerBoardEdge(edge, initialAreaParam.polyOrientation, breakerboardWidth),
            };
          })
        );
      }
      islandAreasParam.push(initialAreaParam);
      // do we need more breaker boards, aside from the closing ones?
      const box = getRotatedBoundingBox(
        initialAreaParam.polygon,
        islandPlanksAngle,
        initialAreaParam.breakerBoardsEdges
      );
      if (box.xminymin.distanceTo(box.xmaxymin)[0] > maxPlankLength) {
        // inner edges always split areas
        const extendedInnerEdges = innerEdges.map(edge => constrainSegmentToBorders(edge, initialPolygon)[0]);
        const notTooCloseExtendedInnerEdges = [];
        extendedInnerEdges.forEach(cut => {
          if (!notTooCloseExtendedInnerEdges.some(cut2 => cut2.distanceTo(cut)[0] < breakerboardWidth)) {
            notTooCloseExtendedInnerEdges.push(cut);
          }
        });
        for (const cut of notTooCloseExtendedInnerEdges) {
          for (const areaParam of islandAreasParam) {
            if (checkIfContains(areaParam.polygon, cut)) {
              const resultAreasState = editState.split(areaParam.id, cut);
              const resultAreaParams = areaParamsFromSplit(areaParam, resultAreasState);
              for (const areaParam of resultAreaParams) {
                for (const edge of [...areaParam.polygon.faces][0]) {
                  if (edge.shape.equalTo(cut) || edge.shape.equalTo(cut.reverse())) {
                    const edgeStartVector = new Vector(new Point(0, 0), edge.shape.ps);
                    const edgeEndVector = new Vector(new Point(0, 0), edge.shape.pe);
                    const edgeVector = new Vector(edge.shape.ps, edge.shape.pe);
                    let displacement;
                    if ([...areaParam.polygon.faces][0].orientation() == ORIENTATION.CW) {
                      displacement = edgeVector
                        .rotate90CW()
                        .normalize()
                        .multiply(breakerboardWidth);
                    } else {
                      displacement = edgeVector
                        .rotate90CCW()
                        .normalize()
                        .multiply(breakerboardWidth);
                    }
                    const futureCutStartVector = edgeStartVector.add(displacement);
                    const futureCutEndVector = edgeEndVector.add(displacement);
                    areaParam.breakerBoardsEdges.push({
                      edge: cut,
                      futureCut: new Segment(
                        new Point(futureCutStartVector.x, futureCutStartVector.y),
                        new Point(futureCutEndVector.x, futureCutEndVector.y)
                      ),
                    });
                    break;
                  }
                }
              }
              islandAreasParam.splice(
                islandAreasParam.findIndex(param => param.id === areaParam.id),
                1,
                ...resultAreaParams
              );
              break;
            }
          }
        }

        // check if any area is too wide for boards, needs more splitting
        islandAreasParam = createSplitsForTooWideAreas(
          islandAreasParam,
          editState,
          maxPlankLength,
          breakerboardWidth,
          islandPlanksAngle
        );
      }
    } else {
      islandAreasParam.push(initialAreaParam);
    }

    islands.push(islandAreasParam);
  }

  for (const island of islands) {
    for (const areaParam of island) {
      for (const [i, edge] of [...[...areaParam.polygon.faces][0]].entries()) {
        if (
          areaParam.breakerBoardsEdges.some(bb => edge.shape.equalTo(bb.edge) || edge.shape.equalTo(bb.edge.reverse()))
        ) {
          editState.placeBreakerBoards(areaParam.id, i, 1);
        }
      }
    }
  }
  // send plan with automatic placement of breaker boards to calculation node
  requester.recalculate(editState.backendObjects, onSuccess, onError, true);
}
